/**
 * Game Author - represents creator (user or team)
 */
export interface GameAuthor {
  id: string;
  name: string;
  image: string | null;
  is_team: boolean;
}

/**
 * Game - matches server catalog response format
 */
export interface Game {
  id: string;
  project_slug: string;
  name: string;
  description: string;
  short_description?: string;
  category: string;
  tags: string[];
  icon_url: string | null;
  cover_image_url: string | null;
  screenshot_urls?: string[];
  play_instructions?: string | null;
  author: GameAuthor | null;
  team_id?: string;
  team_name?: string;
  total_plays: number;
  total_likes: number;
  total_downloads: number;
  total_avg_viewers?: number;
  created_at: string;
  updated_at: string;
  game_url: string;
  version?: string;
  bundle_version?: number;
  required_tier?: string;
  visibility?: string;
  source?: string; // 'cli' or 'studio'
  isActive?: boolean;
  manifest?: {
    beemi: Record<string, unknown>;
  };
  
  // Paid Games Feature
  pricing?: {
    is_paid: boolean;
    price: number;
    currency: string;
  };
  user_owns?: boolean;
}

/**
 * Game category - matches server categories
 */
export type GameCategory = 
  | 'All'
  | 'action'
  | 'adventure'
  | 'arcade'
  | 'board'
  | 'card'
  | 'casino'
  | 'casual'
  | 'creative'
  | 'educational'
  | 'family'
  | 'music'
  | 'party'
  | 'puzzle'
  | 'racing'
  | 'role_playing'
  | 'simulation'
  | 'sports'
  | 'strategy'
  | 'trivia'
  | 'word'
  | 'other';

/**
 * Category metadata for UI display
 */
export interface CategoryInfo {
  id: GameCategory;
  displayName: string;
  description: string;
  icon: string;
}

/**
 * All available categories with metadata
 */
export const GAME_CATEGORIES: CategoryInfo[] = [
  { id: 'All', displayName: 'All', description: 'All games', icon: 'LayoutGrid' },
  { id: 'action', displayName: 'Action', description: 'Fast-paced games requiring quick reflexes', icon: 'Zap' },
  { id: 'adventure', displayName: 'Adventure', description: 'Exploration and story-driven games', icon: 'Map' },
  { id: 'arcade', displayName: 'Arcade', description: 'Classic arcade-style games', icon: 'Gamepad2' },
  { id: 'board', displayName: 'Board', description: 'Digital board games', icon: 'Grid3x3' },
  { id: 'card', displayName: 'Card', description: 'Card games and collections', icon: 'Spade' },
  { id: 'casino', displayName: 'Casino', description: 'Casino and gambling games', icon: 'Dice5' },
  { id: 'casual', displayName: 'Casual', description: 'Easy to play, relaxing games', icon: 'Coffee' },
  { id: 'creative', displayName: 'Creative', description: 'Drawing, building, and creative games', icon: 'Palette' },
  { id: 'educational', displayName: 'Educational', description: 'Learning and educational games', icon: 'GraduationCap' },
  { id: 'family', displayName: 'Family', description: 'Games suitable for all ages', icon: 'Users' },
  { id: 'music', displayName: 'Music', description: 'Rhythm and music games', icon: 'Music' },
  { id: 'party', displayName: 'Party', description: 'Multiplayer party games', icon: 'PartyPopper' },
  { id: 'puzzle', displayName: 'Puzzle', description: 'Logic and puzzle games', icon: 'Puzzle' },
  { id: 'racing', displayName: 'Racing', description: 'Racing and driving games', icon: 'Car' },
  { id: 'role_playing', displayName: 'Role Playing', description: 'RPG and character progression games', icon: 'Swords' },
  { id: 'simulation', displayName: 'Simulation', description: 'Life and world simulation games', icon: 'Globe' },
  { id: 'sports', displayName: 'Sports', description: 'Sports and athletic games', icon: 'Trophy' },
  { id: 'strategy', displayName: 'Strategy', description: 'Strategic thinking and planning games', icon: 'Target' },
  { id: 'trivia', displayName: 'Trivia', description: 'Quiz and trivia games', icon: 'Brain' },
  { id: 'word', displayName: 'Word', description: 'Word puzzles and vocabulary games', icon: 'Type' },
  { id: 'other', displayName: 'Other', description: 'Miscellaneous games', icon: 'MoreHorizontal' },
];

/**
 * Get category display name from ID
 */
export function getCategoryDisplayName(categoryId: string): string {
  const category = GAME_CATEGORIES.find(c => c.id === categoryId);
  return category?.displayName || categoryId;
}

/**
 * Catalog filter options
 */
export interface GameFilters {
  category?: string;
  tags?: string[];
  search?: string;
  featured?: boolean;
}

/**
 * Team info for personalized catalog
 */
export interface Team {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  avatar_url: string | null;
  is_personal: boolean;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  role: string;
  joined_at: string;
}

/**
 * API response wrapper
 */
export interface GamesApiResponse {
  success: boolean;
  data: Game[];
  message?: string;
}

export interface GameApiResponse {
  success: boolean;
  data: Game;
  message?: string;
}

/**
 * Personalized catalog response with team games
 */
export interface PersonalizedCatalogResponse {
  success: boolean;
  data: {
    catalogGames?: Game[];
    games?: Game[];
    sections?: GameSection[];
    privateGames?: Game[];
    unpublishedGames?: Game[];
    userTeams?: Team[];
    teamGames?: { [teamId: string]: Game[] };
  };
  message?: string;
}

/**
 * Game Section - organized grouping of games
 */
export interface GameSection {
  type: 'featured' | 'new' | 'trending' | 'popular' | 'category' | 'recommended';
  title: string;
  games: Game[];
  metadata?: {
    algorithm?: string;
    max_age_days?: number;
    window_days?: number;
    category_id?: string;
    personalization?: boolean;
  };
}